import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import config from '@/config/config';
import { FRESHWORKS } from '@/infrastructure/constants';
import { withSilentAccessToken } from '@/infrastructure/helper';
import useEmbedScript from '@/infrastructure/hooks/useEmbedScript';
import { LtDialog } from '@/components';
import { useAppSelector } from '@/application/hooks';
import { Account } from '@lemontaps/lib/types/api';
import { styled } from '@mui/system';

type Props = {
  onClosed(): void;
  open: boolean;
  tTitle?: string;
};

export default function UpgradeRequestPopup(props: Props) {
  const account = useAppSelector<Account>(state => state.account);
  const ref = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  const activeLanguage = i18n.language.slice(0, 2);

  const {
    location: { pathname },
  } = useHistory();

  const onload = useCallback(() => {
    withSilentAccessToken(getAccessTokenSilently, token =>
      axios.post(
        config.API_BASE_URL + 'account/upgradeIntent',
        {
          pathname,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      ),
    );

    // pre-fill certain values
    const preFillValues = {
      'input[name="contact[custom_field][cf_upgrade__referrer_product_url]"]':
        window.location.pathname,
      'input[name="contact[first_name]"]': account?.firstName,
      'input[name="contact[last_name]"]': account?.lastName,
      'input[name="contact[email]"]': account?.authEmail,
    };
    const manuallyHide = ['input[name="contact[custom_field][cf_upgrade__referrer_product_url]"]'];
    const interval = setInterval(() => {
      if (ref.current) {
        Object.entries(preFillValues).forEach(([selector, value]) => {
          if (!value) return;
          const input = ref.current?.querySelector(selector);
          if (input && 'value' in input && !input.value) {
            input.value = value;
            input.setAttribute('value', value);
          }
        });
        manuallyHide.forEach(selector => {
          const input = ref.current?.querySelector(selector);
          if (input?.parentElement) {
            input.parentElement.style.display = 'none';
          }
        });
      }
    }, 50);
    setTimeout(() => {
      clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [account?.authEmail, account?.firstName, account?.lastName, getAccessTokenSilently, pathname]);

  useEmbedScript(
    FRESHWORKS?.[activeLanguage as keyof typeof FRESHWORKS]?.contactFormUrl,
    ref,
    FRESHWORKS?.[activeLanguage as keyof typeof FRESHWORKS]?.contactFormId,
    'anonymous',
    onload,
  );

  return (
    <LtDialog
      open={props.open}
      title={t(props.tTitle || 'upgradeRequestPopup.title')}
      onClose={props.onClosed}
    >
      <StyledFormWrapper ref={ref}></StyledFormWrapper>
    </LtDialog>
  );
}

const StyledFormWrapper = styled<'div'>('div')({
  '& .powered-by': {
    display: 'none !important',
  },
});
